import React from 'react';
import { Button, InputText, Select } from '@/components';
import { OptionProps } from '@/components/Select';
import { UseFormControl } from '@/types';
import { FormInputNameFixAddress, FormInputNameFixClientData } from '@/features/profile/types';

export interface ErroFields {
  label: string;
  message: string;
}

interface Props {
  state: boolean;
  shouldShowPersonal: boolean | undefined;
  shouldShowAddress: boolean;
  controllerPersonalInfo: UseFormControl;
  controllerAddress: UseFormControl;
  onChangeCEP: (value: string) => void;
  onSubmit: () => void;
  errorFields: ErroFields[];
}

export const FixAccountContent: React.FC<Props> = ({
  state,
  shouldShowPersonal,
  shouldShowAddress,
  controllerPersonalInfo,
  controllerAddress,
  onChangeCEP,
  onSubmit,
  errorFields,
}) => {
  const stateOptions: OptionProps[] = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapa' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceara' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espirito Santo' },
    { value: 'GO', label: 'Goias' },
    { value: 'MA', label: 'Maranhao' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Para' },
    { value: 'PB', label: 'Paraiba' },
    { value: 'PR', label: 'Parana' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piaui' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondonia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'Sao Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' },
  ];

  const disableSubmit =
    state ||
    (shouldShowPersonal &&
      (controllerPersonalInfo.formData[FormInputNameFixClientData.name] === '' ||
        controllerPersonalInfo.formData[FormInputNameFixClientData.email] === '' ||
        controllerPersonalInfo.formData[FormInputNameFixClientData.phone] === '' ||
        controllerPersonalInfo.formData[FormInputNameFixClientData.birthDate] === '' ||
        controllerPersonalInfo.formData[FormInputNameFixClientData.motherName] === '')) ||
    (shouldShowAddress &&
      (controllerAddress.formData[FormInputNameFixAddress.zipCode] === '' ||
        controllerAddress.formData[FormInputNameFixAddress.state] === '' ||
        controllerAddress.formData[FormInputNameFixAddress.city] === '' ||
        controllerAddress.formData[FormInputNameFixAddress.district] === '' ||
        controllerAddress.formData[FormInputNameFixAddress.street] === '' ||
        controllerAddress.formData[FormInputNameFixAddress.number] === ''));

  return (
    <div className="min-h-[calc(100vh-120px)]">
      <div className="container mx-auto px-6 py-16">
        <div className="flex justify-center">
          <div className="w-full max-w-[920px] bg-white shadow-card rounded-xl p-6">
            <h1 className="font-dmsans font-medium text-[28px] text-heading mb-2">
              Ajustar dados da conta
            </h1>
            <p className="font-dmsans text-base text-neutro-w-600 mb-6">
              Precisamos validar algumas informações antes de você continuar.
            </p>

            {errorFields && errorFields.length > 0 && (
              <div className="mb-8 rounded-lg border border-alert-danger-soft bg-alert-danger-soft/10 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-full">
                    <p className="font-dmsans text-sm font-medium text-heading">
                      Precisam de correção
                    </p>
                    <p className="font-dmsans text-xs text-neutro-w-600 mt-1">
                      Revise os campos abaixo e ajuste antes de continuar.
                    </p>

                    <ul className="mt-3 space-y-2">
                      {errorFields.map(errorField => (
                        <li key={errorField.label} className="flex items-start gap-2">
                          <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-alert-danger-soft/70" />
                          <div className="font-dmsans text-sm text-neutro-w-800">
                            <span className="font-semibold text-heading">
                              {String(errorField.label).replace(/_/g, ' ')}
                            </span>
                            <span className="text-neutro-w-600"> — </span>
                            <span className="text-neutro-w-700 text-red-600">
                              {errorField.message}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {shouldShowPersonal && (
              <>
                <h2 className="font-dmsans font-medium text-[18px] mb-6">Dados pessoais</h2>

                <div className="mb-[24px]">
                  <InputText
                    name="name"
                    label="Nome completo"
                    value={controllerPersonalInfo.formData[FormInputNameFixClientData.name]}
                    onChange={e =>
                      controllerPersonalInfo.onChangeFormInput(FormInputNameFixClientData.name)(
                        e.target.value,
                      )
                    }
                    error={
                      controllerPersonalInfo.formErrors.name &&
                      controllerPersonalInfo.formErrors.name[0]
                    }
                  />
                </div>

                <div className="mb-[24px]">
                  <InputText
                    name="email"
                    label="E-mail"
                    value={controllerPersonalInfo.formData[FormInputNameFixClientData.email]}
                    onChange={e =>
                      controllerPersonalInfo.onChangeFormInput(FormInputNameFixClientData.email)(
                        e.target.value,
                      )
                    }
                    error={
                      controllerPersonalInfo.formErrors.email &&
                      controllerPersonalInfo.formErrors.email[0]
                    }
                  />
                </div>

                <div className="mb-[24px]">
                  <InputText
                    name="phone"
                    label="Telefone"
                    value={controllerPersonalInfo.formData[FormInputNameFixClientData.phone]}
                    onChange={e =>
                      controllerPersonalInfo.onChangeFormInput(FormInputNameFixClientData.phone)(
                        e.target.value,
                      )
                    }
                    error={
                      controllerPersonalInfo.formErrors.phone &&
                      controllerPersonalInfo.formErrors.phone[0]
                    }
                  />
                </div>

                <div className="mb-[24px]">
                  <InputText
                    name="birthDate"
                    label="Data de nascimento"
                    placeholder="DD/MM/AAAA"
                    value={controllerPersonalInfo.formData[FormInputNameFixClientData.birthDate]}
                    onChange={e =>
                      controllerPersonalInfo.onChangeFormInput(
                        FormInputNameFixClientData.birthDate,
                      )(e.target.value)
                    }
                    error={
                      controllerPersonalInfo.formErrors.birthDate &&
                      controllerPersonalInfo.formErrors.birthDate[0]
                    }
                  />
                </div>

                <div className="mb-[40px]">
                  <InputText
                    name="motherName"
                    label="Nome da mãe"
                    value={controllerPersonalInfo.formData[FormInputNameFixClientData.motherName]}
                    onChange={e =>
                      controllerPersonalInfo.onChangeFormInput(
                        FormInputNameFixClientData.motherName,
                      )(e.target.value)
                    }
                    error={
                      controllerPersonalInfo.formErrors.motherName &&
                      controllerPersonalInfo.formErrors.motherName[0]
                    }
                  />
                </div>
              </>
            )}

            {shouldShowAddress && (
              <>
                <h2 className="font-dmsans font-medium text-[18px] mb-6">Endereço</h2>

                <div className="mb-[24px]">
                  <InputText
                    name="zipCode"
                    label="CEP"
                    maxLength={9}
                    value={controllerAddress.formData[FormInputNameFixAddress.zipCode]}
                    onChange={e => {
                      controllerAddress.onChangeFormInput(FormInputNameFixAddress.zipCode)(
                        e.target.value,
                      );
                      onChangeCEP(e.target.value);
                    }}
                    error={
                      controllerAddress.formErrors.zipCode &&
                      controllerAddress.formErrors.zipCode[0]
                    }
                  />
                </div>

                <div className="mb-[24px] grid grid-cols-5 gap-5">
                  <Select
                    name="state"
                    label="Estado"
                    placeholder="Selecione..."
                    value={controllerAddress.formData[FormInputNameFixAddress.state]}
                    onChange={e =>
                      controllerAddress.onChangeFormInput(FormInputNameFixAddress.state)(
                        e?.target.value as string,
                      )
                    }
                    className="col-span-2"
                    error={
                      controllerAddress.formErrors.state && controllerAddress.formErrors.state[0]
                    }
                    options={stateOptions}
                  />
                  <InputText
                    name="city"
                    label="Cidade"
                    className="col-span-3"
                    value={controllerAddress.formData[FormInputNameFixAddress.city]}
                    onChange={e =>
                      controllerAddress.onChangeFormInput(FormInputNameFixAddress.city)(
                        e.target.value,
                      )
                    }
                    error={
                      controllerAddress.formErrors.city && controllerAddress.formErrors.city[0]
                    }
                  />
                </div>

                <div className="mb-[24px]">
                  <InputText
                    name="district"
                    label="Bairro"
                    value={controllerAddress.formData[FormInputNameFixAddress.district]}
                    onChange={e =>
                      controllerAddress.onChangeFormInput(FormInputNameFixAddress.district)(
                        e.target.value,
                      )
                    }
                    error={
                      controllerAddress.formErrors.district &&
                      controllerAddress.formErrors.district[0]
                    }
                  />
                </div>

                <div className="mb-[24px] grid grid-cols-4 gap-5">
                  <InputText
                    name="street"
                    label="Rua/Avenida"
                    className="col-span-3"
                    value={controllerAddress.formData[FormInputNameFixAddress.street]}
                    onChange={e =>
                      controllerAddress.onChangeFormInput(FormInputNameFixAddress.street)(
                        e.target.value,
                      )
                    }
                    error={
                      controllerAddress.formErrors.street && controllerAddress.formErrors.street[0]
                    }
                  />
                  <InputText
                    name="number"
                    label="Número"
                    value={controllerAddress.formData[FormInputNameFixAddress.number]}
                    onChange={e =>
                      controllerAddress.onChangeFormInput(FormInputNameFixAddress.number)(
                        e.target.value,
                      )
                    }
                    error={
                      controllerAddress.formErrors.number && controllerAddress.formErrors.number[0]
                    }
                  />
                </div>

                <div className="mb-[40px]">
                  <InputText
                    name="complement"
                    label="Complemento (opcional)"
                    value={controllerAddress.formData[FormInputNameFixAddress.complement]}
                    onChange={e =>
                      controllerAddress.onChangeFormInput(FormInputNameFixAddress.complement)(
                        e.target.value,
                      )
                    }
                    error={
                      controllerAddress.formErrors.complement &&
                      controllerAddress.formErrors.complement[0]
                    }
                  />
                </div>
              </>
            )}

            <Button
              type="button"
              title="Salvar e continuar"
              buttonStyle="primary"
              size="xlg"
              className="w-full"
              disabled={disableSubmit}
              onClick={onSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
